import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import Modal from "../../components/Modal";
import { useToast } from "../../lib/toast";

interface Movement {
  id: string; product_id: string; sku: string; product_name: string;
  type: "in" | "out" | "adjust"; qty: number; reason: string | null;
  ref_type: string | null; ref_id: string | null;
  actor_id: string | null; created_at: number;
}
interface ProductLite { id: string; sku: string; name: string; unit: string; stock: number; }

const fmt = (n: number) => n.toLocaleString("ko-KR");

export default function AdminStock() {
  const [moves, setMoves] = useState<Movement[]>([]);
  const [total, setTotal] = useState(0);
  const [products, setProducts] = useState<ProductLite[]>([]);
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get<{ items: Movement[]; total: number }>("/admin/stock?limit=50"),
      api.get<{ items: ProductLite[] }>("/products"),
    ]).then(([m, p]) => {
      setMoves(m.items);
      setTotal(m.total);
      setProducts(p.items);
    }).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const r = await api.get<{ items: Movement[]; total: number }>(`/admin/stock?limit=50&offset=${moves.length}`);
      setMoves((prev) => [...prev, ...r.items]);
      setTotal(r.total);
    } finally { setLoadingMore(false); }
  };

  return (
    <>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 16 }}>
        <h2 className="h-section" style={{ margin: 0 }}>재고 이력 ({moves.length}/{total}건)</h2>
        <button className="btn btn-primary btn-sm" onClick={() => setAdding(true)}>+ 재고 조정</button>
      </div>

      <div className="card" style={{ overflowX: "auto" }}>
        <table>
          <thead><tr><th>일시</th><th>SKU</th><th>상품</th><th>유형</th><th style={{ textAlign: "right" }}>수량 변화</th><th>사유</th><th>참조</th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ padding: 40, textAlign: "center" }} className="muted">로딩 중...</td></tr>
            ) : moves.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: 40, textAlign: "center" }} className="muted">이력이 없습니다.</td></tr>
            ) : moves.map((m) => (
              <tr key={m.id}>
                <td style={{ fontSize: 15 }} className="muted">{new Date(m.created_at * 1000).toLocaleString("ko-KR")}</td>
                <td className="mono" style={{ fontSize: 15 }}>{m.sku}</td>
                <td style={{ fontSize: 15 }}>{m.product_name}</td>
                <td><span className={`badge ${m.type === "in" ? "badge-success" : m.type === "out" ? "badge-warning" : "badge-muted"}`}>{m.type}</span></td>
                <td style={{ textAlign: "right", fontWeight: 600, color: m.qty > 0 ? "var(--success)" : m.qty < 0 ? "var(--danger)" : "var(--text-muted)" }}>
                  {m.qty > 0 ? "+" : ""}{fmt(m.qty)}
                </td>
                <td style={{ fontSize: 15 }}>{m.reason ?? "—"}</td>
                <td style={{ fontSize: 15 }} className="muted">{m.ref_type}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!loading && moves.length < total && (
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <button className="btn btn-outline btn-sm" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? "불러오는 중..." : `더 보기 (${moves.length}/${total})`}
          </button>
        </div>
      )}

      {adding && <MoveEditor products={products} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
    </>
  );
}

function MoveEditor({ products, onClose, onSaved }: { products: ProductLite[]; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({ product_id: products[0]?.id ?? "", type: "in" as "in" | "out" | "adjust", qty: 10, reason: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { push } = useToast();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      await api.post("/admin/stock/movement", form);
      push("재고가 조정되었습니다", "success");
      onSaved();
    } catch (e: any) { setErr(e.message); push(e.message, "error"); } finally { setBusy(false); }
  };

  return (
    <Modal title="재고 조정" subtitle="입고·출고·조정 기록 (이력 테이블에 영구 저장)" onClose={onClose} width={480}>
      <form onSubmit={submit} className="stack">
          <label>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>상품</div>
            <select value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })}>
              {products.map((p) => <option key={p.id} value={p.id}>[{p.sku}] {p.name} (현재고 {p.stock})</option>)}
            </select>
          </label>
          <label>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>유형</div>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as any })}>
              <option value="in">입고 (+)</option>
              <option value="out">출고 (-)</option>
              <option value="adjust">조정 (부호 유지: 양수=증가, 음수=감소)</option>
            </select>
          </label>
          <label>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>수량 {form.type === "adjust" ? "(부호 포함)" : ""}</div>
            <input type="number" value={form.qty} onChange={(e) => setForm({ ...form, qty: Number(e.target.value) })} required />
          </label>
          <label>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>사유</div>
            <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="예) 매입처 S-001 입고, 파손 폐기" />
          </label>
          {err && <div style={{ padding: 10, background: "var(--danger-soft)", color: "var(--danger)", borderRadius: 4, fontSize: 15 }}>{err}</div>}
          <div className="row">
            <button type="button" className="btn btn-outline" onClick={onClose} style={{ flex: 1 }}>취소</button>
            <button className="btn btn-primary" style={{ flex: 1 }} disabled={busy}>{busy ? "저장 중..." : "적용"}</button>
          </div>
        </form>
    </Modal>
  );
}
